/**
 * Incident Communications Service for Outlook Add-in
 * Handles incident communications thread data fetching from DoNexus API
 */

import apiClient from '../utils/apiClient';

class IncidentCommunicationsService {
  constructor() {
    this.threadId = null;
    this.messageId = null;
    this.cachedMessageData = null;
    this.cachedMessageId = null;
    this.cachedIncidentId = null;
    this.cachedPropertyId = null;
    this.pendingRequest = null; // To prevent duplicate simultaneous requests
  }

  /**
   * Get the Office.js message ID from the current email
   * @returns {string|null} Message ID or null if not available
   */
  getOfficeMessageId() {
    try {
      // Check if Office.js is available and we're in an email context
      if (typeof Office !== 'undefined' && 
          Office.context && 
          Office.context.mailbox && 
          Office.context.mailbox.item && 
          Office.context.mailbox.item.itemId) {
        return Office.context.mailbox.item.itemId;
      }
      return null;
    } catch (error) {
      console.error('Error getting Office message ID:', error);
      return null;
    }
  }

  /**
   * Get the Office.js thread ID from the current email conversation
   * @returns {string|null} Thread ID or null if not available
   */
  getOfficeThreadId() {
    try {
      // Check if Office.js is available and we're in an email context
      if (typeof Office !== 'undefined' && 
          Office.context && 
          Office.context.mailbox && 
          Office.context.mailbox.item && 
          Office.context.mailbox.item.conversationId) {
        return Office.context.mailbox.item.conversationId;
      }
      return null;
    } catch (error) {
      console.error('Error getting Office thread ID:', error);
      return null;
    }
  }

  /**
   * Initialize message ID from Office.js context
   * @returns {boolean} True if message ID was successfully set, false otherwise
   */
  initializeMessageId() {
    const officeMessageId = this.getOfficeMessageId();
    const normalizeId = (id) => id ? id.replace(/\+/g, '_').replace(/\//g, '-') : null;
    if (officeMessageId) {
      this.messageId = normalizeId(officeMessageId);
      console.log('Message ID initialized and normalized from Office.js:', this.messageId);
      return true;
    } else {
      console.warn('Could not get message ID from Office.js context');
      return false;
    }
  }

  /**
   * Initialize thread ID from Office.js context
   * @returns {boolean} True if thread ID was successfully set, false otherwise
   */
  initializeThreadId() {
    const officeThreadId = this.getOfficeThreadId();
    const normalizeId = (id) => id ? id.replace(/\+/g, '_').replace(/\//g, '-') : null;
    if (officeThreadId) {
      this.threadId = normalizeId(officeThreadId);
      console.log('Thread ID initialized and normalized from Office.js:', this.threadId);
      return true;
    } else {
      console.warn('Could not get thread ID from Office.js context');
      return false;
    }
  }

  /**
   * Fetch incident communications data by message ID with smart caching
   * @param {boolean} forceRefresh - Force a fresh fetch even if cached data exists
   * @returns {Promise<Object>} Message data object with incident_id, property_id, contact_id
   */
  async getThreadData(forceRefresh = false) {
    try {
      // Ensure message ID is initialized from Office.js context
      if (!this.messageId) {
        const initialized = this.initializeMessageId();
        if (!initialized) {
          throw new Error('Could not get message ID from Office.js context');
        }
      }

      // Also initialize thread ID for tracking
      if (!this.threadId) {
        this.initializeThreadId();
      }

      // Return cached data if available and message hasn't changed
      if (!forceRefresh && this.cachedMessageData && this.cachedMessageId === this.messageId) {
        console.log('✅ Returning cached message data for:', this.messageId);
        return this.cachedMessageData;
      }

      // If there's already a pending request for this message, return it
      if (this.pendingRequest && this.cachedMessageId === this.messageId) {
        console.log('🔄 Reusing pending request for:', this.messageId);
        return await this.pendingRequest;
      }

      // Determine what needs to be refetched based on thread context
      let refetchStrategy = 'full'; // Default: fetch everything
      
      if (this.cachedMessageData && this.cachedIncidentId && this.cachedPropertyId) {
        // We have previous data - determine if we're in the same thread or different thread
        // Since we're switching messages, we need to check the new message's context
        refetchStrategy = 'check'; // We'll check after fetching
        console.log('📋 Previous context exists - will determine refetch strategy after fetch');
      }

      // Clear message cache if message ID changed
      if (this.cachedMessageId !== this.messageId) {
        console.log('📧 Message ID changed from', this.cachedMessageId, 'to', this.messageId);
        this.cachedMessageId = this.messageId;
      }

      // Create new request
      console.log('🔍 Fetching message data for:', this.messageId);
      this.pendingRequest = (async () => {
        try {
          const response = await apiClient.get(`/incident-communications/message/${this.messageId}`);
          
          // Handle success or "not found" gracefully
          if (response.success || (response.success === false && response.message === "Incident communication index not found")) {
            // If not found, create empty message data structure
            const isNotFound = response.success === false;
            
            if (isNotFound) {
              console.warn('⚠️ Incident communication index not found - working without thread info');
            }
            
            const messageData = {
              success: !isNotFound,
              data: isNotFound ? null : response.data,
              message: response.message,
              notFound: isNotFound,
              refetchInfo: {
                isNewThread: false,
                needsFullRefetch: isNotFound, // Treat as new/empty state
                needsContactRefetch: isNotFound
              }
            };

            // Extract incident_id and property_id from the new data
            const newIncidentId = isNotFound ? null : this.extractIncidentId(messageData);
            const newPropertyId = isNotFound ? null : this.extractPropertyId(messageData);

            // Determine if we switched threads
            if (!isNotFound && this.cachedIncidentId && this.cachedPropertyId) {
              const isSameThread = 
                newIncidentId === this.cachedIncidentId && 
                newPropertyId === this.cachedPropertyId;

              if (!isSameThread) {
                console.log('🔀 DIFFERENT THREAD detected - full refetch needed');
                console.log('  Previous:', { incident_id: this.cachedIncidentId, property_id: this.cachedPropertyId });
                console.log('  Current:', { incident_id: newIncidentId, property_id: newPropertyId });
                messageData.refetchInfo.isNewThread = true;
                messageData.refetchInfo.needsFullRefetch = true;
                messageData.refetchInfo.needsContactRefetch = true;
              } else {
                console.log('✅ SAME THREAD - only contact_id might have changed');
                messageData.refetchInfo.isNewThread = false;
                messageData.refetchInfo.needsFullRefetch = false;
                messageData.refetchInfo.needsContactRefetch = true; // Contact might change within thread
              }
            } else if (!isNotFound) {
              // First fetch - need everything
              console.log('🆕 FIRST FETCH - full refetch needed');
              messageData.refetchInfo.needsFullRefetch = true;
              messageData.refetchInfo.needsContactRefetch = true;
            }

            // Update cached incident_id and property_id
            if (!isNotFound) {
              this.cachedIncidentId = newIncidentId;
              this.cachedPropertyId = newPropertyId;
            }
            
            // Cache the response
            this.cachedMessageData = messageData;
            
            return messageData;
          } else {
            throw new Error(response.message || 'Failed to fetch message data');
          }
        } finally {
          // Clear pending request after completion
          this.pendingRequest = null;
        }
      })();

      return await this.pendingRequest;
    } catch (error) {
      console.error('❌ Error fetching message data:', error);
      this.pendingRequest = null;
      throw error;
    }
  }

  /**
   * Clear cached message data (useful when switching messages/threads)
   */
  clearCache() {
    console.log('🧹 Clearing message data cache');
    this.cachedMessageData = null;
    this.cachedMessageId = null;
    this.cachedIncidentId = null;
    this.cachedPropertyId = null;
    this.pendingRequest = null;
  }

  /**
   * Get message ID
   * @returns {string|null} Current message ID
   */
  getMessageId() {
    return this.messageId;
  }

  /**
   * Check if message ID is available and valid
   * @returns {boolean} True if message ID is available
   */
  isMessageIdAvailable() {
    return this.messageId !== null && this.messageId !== undefined && this.messageId !== '';
  }

  /**
   * Extract property ID from message data
   * @param {Object} messageData - Message data from API
   * @returns {string|null} Property ID or null if not found
   */
  extractPropertyId(messageData) {
    if (messageData && messageData.data) {
      // Handle both single object and array responses
      const data = Array.isArray(messageData.data) ? messageData.data[0] : messageData.data;
      return data?.property_id || null;
    }
    return null;
  }

  /**
   * Extract contact ID from message data
   * @param {Object} messageData - Message data from API
   * @returns {string|null} Contact ID or null if not found
   */
  extractContactId(messageData) {
    if (messageData && messageData.data) {
      // Handle both single object and array responses
      const data = Array.isArray(messageData.data) ? messageData.data[0] : messageData.data;
      const contactIds = data?.contact_ids;
      if (contactIds && Array.isArray(contactIds) && contactIds.length > 0) {
        return contactIds[0];
      }
      // Fallback: check for single contact_id field
      return data?.contact_id || null;
    }
    return null;
  }

  /**
   * Extract incident ID from message data
   * @param {Object} messageData - Message data from API
   * @returns {string|null} Incident ID or null if not found
   */
  extractIncidentId(messageData) {
    if (messageData && messageData.data) {
      // Handle both single object and array responses
      const data = Array.isArray(messageData.data) ? messageData.data[0] : messageData.data;
      return data?.incident_id || null;
    }
    return null;
  }

  /**
   * Get thread ID
   * @returns {string|null} Current thread ID
   */
  getThreadId() {
    return this.threadId;
  }

  /**
   * Check if thread ID is available and valid
   * @returns {boolean} True if thread ID is available
   */
  isThreadIdAvailable() {
    return this.threadId !== null && this.threadId !== undefined && this.threadId !== '';
  }

  /**
   * Extract action prompts from message data
   * @param {Object} messageData - Message data from API
   * @returns {Array} Array of action prompts or empty array if not found
   */
  extractActionPrompts(messageData) {
    if (messageData && messageData.data) {
      // Handle both single object and array responses
      const data = Array.isArray(messageData.data) ? messageData.data[0] : messageData.data;
      const actionPrompts = data?.action_prompts;
      if (actionPrompts && typeof actionPrompts === 'object') {
        // Convert action_prompts object to array format
        return Object.entries(actionPrompts).map(([key, value]) => ({
          id: key,
          display_action: value.display_action,
          action_prompt: value.action_prompt,
          question_for_details: value.question_for_details
        }));
      }
    }
    return [];
  }

  /**
   * Get refetch information from the last fetch
   * @returns {Object} Refetch info with flags for what needs to be refetched
   */
  getRefetchInfo() {
    if (this.cachedMessageData && this.cachedMessageData.refetchInfo) {
      return this.cachedMessageData.refetchInfo;
    }
    // Default: everything needs to be fetched
    return {
      isNewThread: true,
      needsFullRefetch: true,
      needsContactRefetch: true
    };
  }

  /**
   * Set thread ID (for future use when thread selection is implemented)
   * @param {string} id - Thread ID
   */
  setThreadId(id) {
    this.threadId = id;
    console.log('Thread ID manually set to:', this.threadId);
  }

  /**
   * Fetch latest communications threads with full message history
   * @param {Object} params - Query parameters for filtering
   * @param {string} params.contactId - Contact ID to filter by
   * @param {Array<string>} params.connectionIds - Connection IDs to filter by
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.pageSize - Number of items per page (default: 5)
   * @returns {Promise<Object>} Communications data with pagination
   */
  async getLatestThreadsFull(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination
      queryParams.append('page', params.page?.toString() || '1');
      queryParams.append('pageSize', params.pageSize?.toString() || '5');
      
      // Add contact ID filter
      if (params.contactId) {
        queryParams.append('contact_id', params.contactId);
      }
      
      // Add connection IDs filter
      if (params.connectionIds && Array.isArray(params.connectionIds)) {
        params.connectionIds.forEach(id => {
          queryParams.append('connectionId', id);
        });
      }
      
      // Add other optional filters
      if (params.channel) queryParams.append('channel', params.channel);
      if (params.direction) queryParams.append('direction', params.direction);
      if (params.status) queryParams.append('status', params.status);
      if (params.propertyId) queryParams.append('propertyId', params.propertyId);
      if (params.incidentId) queryParams.append('incidentId', params.incidentId);
      if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
      if (params.dateTo) queryParams.append('dateTo', params.dateTo);
      
      const response = await apiClient.get(`/incident-communications/threads/latest/full?${queryParams.toString()}`);
      
      if (response.success) {
        return {
          success: true,
          data: response.data || [],
          pagination: response.pagination || { page: 1, pageSize: 5, hasMore: false }
        };
      } else {
        throw new Error(response.message || 'Failed to fetch communications');
      }
    } catch (error) {
      console.error('Error fetching latest threads:', error);
      throw error;
    }
  }
}

// Create singleton instance
const incidentCommunicationsService = new IncidentCommunicationsService();

export default incidentCommunicationsService;
